/**
 * Fonctions de génération d'emails pour les prospects (preview frontend uniquement).
 * Doit rester en miroir avec `supabase/functions/send-lead-email/index.ts`.
 */

import { LEAD_SENDERS, type LeadSender, type LeadSenderKey } from "./leadSenders";

export interface LeadForEmail {
  full_name: string;
  phone?: string | null;
  establishment_name?: string | null;
  city?: string | null;
  current_booking_channels?: string[] | null;
  current_management_system?: string[] | null;
}

export type LeadEmailType = 'first_contact' | 'followup' | 'post_call' | 'post_call_failed' | 'post_call_success' | 'custom';

export interface PostCallSuccessOptions {
  zoomLink: string;
  meetingDateTime: string;
}

export interface CustomEmailOptions {
  subject: string;
  body: string;
}

function getFirstName(fullName: string): string {
  return fullName.split(" ")[0];
}

function formatManagementSystem(systems: string[] | null | undefined): string {
  if (!systems || systems.length === 0) return "";
  const labels: Record<string, string> = {
    "cahier_/_papier": "cahier/papier",
    "excel_/_google_sheets": "Excel/Google Sheets",
    "logiciel_spécialisé": "logiciel spécialisé",
    "plateforme_de_réservation": "plateforme de réservation",
    "autre": "autre système",
  };
  return systems.map(s => labels[s] || s).join(", ");
}

function getLeadUnsubscribeFooter(unsubscribeUrl: string): string {
  return `
  <p style="margin-top: 32px; font-size: 11px; color: #9ca3af;">
    <a href="${unsubscribeUrl}" style="color: #9ca3af; text-decoration: none;">Se désinscrire</a>
  </p>
  `;
}

function renderSignature(sender: LeadSender): string {
  const phoneLine = sender.phone
    ? `<a href="tel:${sender.phone.replace(/\s/g, "")}">${sender.phone}</a><br>`
    : "";
  return `
    <p>
      <strong>${sender.fullName}</strong><br>
      ${sender.title}<br>
      ${phoneLine}<a href="mailto:${sender.email}">${sender.email}</a><br>
      <a href="https://piktable.ch">piktable.ch</a>
    </p>
  `;
}

function renderCompactSignature(sender: LeadSender): string {
  const phoneLine = sender.phone ? `<br>${sender.phone}` : "";
  return `
  <p>
    <strong>${sender.fullName}</strong><br>
    ${sender.title}${phoneLine}
  </p>
  `;
}

function textToHtml(text: string): string {
  const urlPattern = /(https?:\/\/[^\s<]+)/g;
  let html = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  html = html.replace(urlPattern, '<a href="$1" style="color: #78350f;">$1</a>');
  const paragraphs = html.split(/\n\s*\n/);
  html = paragraphs.map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('\n');
  return html;
}

export function generateCustomEmail(lead: LeadForEmail, options: CustomEmailOptions, sender: LeadSender = LEAD_SENDERS.maxime, unsubscribeUrl?: string): { subject: string; html: string } {
  const bodyHtml = textToHtml(options.body);
  const unsubscribeFooter = unsubscribeUrl ? getLeadUnsubscribeFooter(unsubscribeUrl) : "";

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333;">
  ${bodyHtml}

  <div style="margin-top: 32px; padding-top: 16px; border-top: 1px solid #eee;">
    ${renderCompactSignature(sender)}
  </div>

  ${unsubscribeFooter}
</body>
</html>
  `.trim();

  return { subject: options.subject, html };
}

export function generateFirstContactEmail(lead: LeadForEmail, sender: LeadSender = LEAD_SENDERS.maxime, unsubscribeUrl?: string): { subject: string; html: string } {
  const firstName = getFirstName(lead.full_name);
  const establishmentName = lead.establishment_name;
  const city = lead.city;
  const managementSystem = formatManagementSystem(lead.current_management_system);

  let inscriptionContext = "";
  if (establishmentName && city) {
    inscriptionContext = ` pour <strong>${establishmentName}</strong> à ${city}`;
  } else if (establishmentName) {
    inscriptionContext = ` pour <strong>${establishmentName}</strong>`;
  }

  let situationLine = "";
  if (managementSystem.includes("cahier") || managementSystem.includes("papier")) {
    situationLine = `<p>Vous gérez aujourd'hui vos réservations sur papier — Piktable garde cette simplicité, en automatisant ce qui prend du temps.</p>`;
  } else if (managementSystem.includes("Excel") || managementSystem.includes("Sheets")) {
    situationLine = `<p>Vous utilisez un tableur — Piktable reprend cette logique, en y ajoutant le widget en ligne, les emails automatiques et le plan de salle visuel.</p>`;
  } else if (managementSystem.includes("plateforme")) {
    situationLine = `<p>Vous utilisez déjà une plateforme de réservation — Piktable est une alternative suisse avec une tarification plafonnée et plus de flexibilité.</p>`;
  }

  const subject = establishmentName
    ? `Votre accès à Piktable - ${establishmentName}`
    : "Bienvenue sur Piktable";

  const ctaSecondary = sender.calendly
    ? ` · <a href="${sender.calendly}" style="color: #78350f; text-decoration: underline;">Réserver un appel découverte (15 min)</a>`
    : "";

  const unsubscribeFooter = unsubscribeUrl ? getLeadUnsubscribeFooter(unsubscribeUrl) : "";

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <style>
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; max-width: 600px; margin: 0 auto; padding: 20px; }
    p { margin: 16px 0; }
    .signature { margin-top: 32px; padding-top: 16px; border-top: 1px solid #eee; }
    .cta-primary { display: inline-block; background: #78350f; color: white !important; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500; }
  </style>
</head>
<body>
  <p>Bonjour ${firstName},</p>

  <p>Suite à votre intérêt${inscriptionContext}, je voulais vous écrire personnellement.</p>

  ${situationLine}

  <p>Concrètement, <strong>Piktable vous fait gagner du temps</strong> : les réservations arrivent toutes seules, les rappels partent automatiquement, et vous gardez la main sur votre plan de salle. Tarif <strong>plafonné</strong> — vous ne payez jamais plus, quel que soit votre volume.</p>

  <p>Vous pouvez tester gratuitement pendant 30 jours :</p>

  <p><a href="https://piktable.ch/signup" class="cta-primary">Créer mon compte</a>${ctaSecondary}</p>

  <p>À votre disposition,</p>

  <div class="signature">
    ${renderSignature(sender)}
  </div>

  ${unsubscribeFooter}
</body>
</html>
  `.trim();

  return { subject, html };
}

export function generateFollowupEmail(lead: LeadForEmail, sender: LeadSender = LEAD_SENDERS.maxime, unsubscribeUrl?: string): { subject: string; html: string } {
  const firstName = getFirstName(lead.full_name);
  const subject = `Avez-vous eu le temps de regarder ?${lead.establishment_name ? ` - ${lead.establishment_name}` : ""}`;
  const unsubscribeFooter = unsubscribeUrl ? getLeadUnsubscribeFooter(unsubscribeUrl) : "";

  const calendlyLine = sender.calendly
    ? `<p>Si vous préférez qu'on en parle de vive voix, n'hésitez pas à me ${sender.phone ? "rappeler ou à " : ""}réserver un créneau ici : <a href="${sender.calendly}">${sender.calendly}</a></p>`
    : `<p>Si vous préférez qu'on en parle de vive voix, n'hésitez pas à me répondre directement à cet email.</p>`;

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333;">
  <p>Bonjour ${firstName},</p>

  <p>Je me permets de vous relancer suite à mon précédent message.</p>

  <p>Avez-vous eu le temps de jeter un œil à Piktable ? Je serais ravi d'en discuter avec vous si vous avez des questions.</p>

  <p>Ce qui fait la différence pour nos clients : les réservations arrivent automatiquement, les rappels partent tout seuls, et le plan de salle vous permet de visualiser votre service en un coup d'œil.</p>

  <p>Emma de Sisterfields m'a dit récemment : <em>"Depuis qu'on utilise Piktable, je passe zéro minute sur les réservations. Ça a vraiment changé mon quotidien."</em></p>

  <p>Vous pouvez créer votre compte gratuitement ici : <a href="https://piktable.ch/signup">https://piktable.ch/signup</a> (30 jours d'essai, sans engagement)</p>

  ${calendlyLine}

  <p>Bonne journée,</p>

  ${renderSignature(sender)}

  ${unsubscribeFooter}
</body>
</html>
  `.trim();

  return { subject, html };
}

export function generatePostCallEmail(lead: LeadForEmail, sender: LeadSender = LEAD_SENDERS.maxime, unsubscribeUrl?: string): { subject: string; html: string } {
  const firstName = getFirstName(lead.full_name);
  const phone = lead.phone;
  const subject = `J'ai essayé de vous joindre${lead.establishment_name ? ` - ${lead.establishment_name}` : ""}`;
  const phonePhrase = phone
    ? `Je viens d'essayer de vous joindre sur votre téléphone au ${phone}.`
    : `Je viens d'essayer de vous joindre par téléphone.`;
  const unsubscribeFooter = unsubscribeUrl ? getLeadUnsubscribeFooter(unsubscribeUrl) : "";

  const calendlyBlock = sender.calendly
    ? `<p>Vous pouvez réserver un appel de 30 minutes ici avec moi : <a href="${sender.calendly}" style="color: #78350f;">${sender.calendly}</a></p>`
    : `<p>N'hésitez pas à me répondre directement à cet email pour fixer un rendez-vous.</p>`;

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333;">
  <p>Bonjour ${firstName},</p>

  <p>${phonePhrase}</p>

  <p>Je voulais simplement savoir si vous aviez des questions sur la façon dont Piktable peut vous aider à gérer vos réservations.</p>

  <p>Piktable, c'est l'accès à vos données clients et des tarifs imbattables, transparents et plafonnés.</p>

  <p>Avec Piktable, vos clients sont autonomes pour réserver, modifier ou annuler leur réservation, sans vous prendre plus de temps.</p>

  <p>Nous vous aidons à configurer et à prendre en main l'outil, et vous accompagnons durant la période de transition de 30 jours, tout cela gratuitement, sans engagement !</p>

  ${calendlyBlock}

  <p>Ou, si vous êtes prêt à essayer directement : <a href="https://piktable.ch/signup" style="display: inline-block; background: #78350f; color: white !important; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 500;">Créer mon compte</a></p>

  <p>À bientôt,</p>

  ${renderCompactSignature(sender)}

  ${unsubscribeFooter}
</body>
</html>
  `.trim();

  return { subject, html };
}

export function generatePostCallFailedEmail(lead: LeadForEmail, sender: LeadSender = LEAD_SENDERS.maxime, unsubscribeUrl?: string): { subject: string; html: string } {
  const firstName = getFirstName(lead.full_name);
  const subject = `Suite à notre échange${lead.establishment_name ? ` - ${lead.establishment_name}` : ""}`;
  const unsubscribeFooter = unsubscribeUrl ? getLeadUnsubscribeFooter(unsubscribeUrl) : "";

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333;">
  <p>Bonjour ${firstName},</p>

  <p>Merci pour notre échange de tout à l'heure.</p>

  <p>Je reste à votre disposition si vous avez des questions par la suite.</p>

  <p>En attendant, vous trouverez toutes les informations sur notre site : <a href="https://piktable.ch" style="color: #78350f;">piktable.ch</a></p>

  <p>À bientôt,</p>

  ${renderCompactSignature(sender)}

  ${unsubscribeFooter}
</body>
</html>
  `.trim();

  return { subject, html };
}

export function generatePostCallSuccessEmail(lead: LeadForEmail, options: PostCallSuccessOptions, sender: LeadSender = LEAD_SENDERS.maxime, unsubscribeUrl?: string): { subject: string; html: string } {
  const firstName = getFirstName(lead.full_name);
  const subject = `Notre rendez-vous Piktable${lead.establishment_name ? ` - ${lead.establishment_name}` : ""}`;
  const unsubscribeFooter = unsubscribeUrl ? getLeadUnsubscribeFooter(unsubscribeUrl) : "";

  const html = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333;">
  <p>Bonjour ${firstName},</p>

  <p>Suite à notre échange, je vous confirme notre rendez-vous :</p>

  <p style="background: #f3f4f6; padding: 16px; border-radius: 8px; margin: 16px 0;">
    📅 <strong>${options.meetingDateTime}</strong><br>
    📍 Zoom : <a href="${options.zoomLink}" style="color: #78350f;">${options.zoomLink}</a>
  </p>

  <p>L'objectif de cet appel sera de :</p>
  <ul>
    <li>Répondre à vos questions</li>
    <li>Vous faire une démo de Piktable</li>
    <li>Découvrir comment vous gérez vos réservations aujourd'hui</li>
  </ul>

  <p>Je me réjouis de vous rencontrer !</p>

  <p>À très vite,</p>

  ${renderCompactSignature(sender)}

  ${unsubscribeFooter}
</body>
</html>
  `.trim();

  return { subject, html };
}

export function generateLeadEmail(
  lead: LeadForEmail,
  emailType: LeadEmailType,
  options?: PostCallSuccessOptions | CustomEmailOptions,
  sender: LeadSender = LEAD_SENDERS.maxime
): { subject: string; html: string } {
  if (emailType === "first_contact") return generateFirstContactEmail(lead, sender);
  if (emailType === "post_call") return generatePostCallEmail(lead, sender);
  if (emailType === "post_call_failed") return generatePostCallFailedEmail(lead, sender);
  if (emailType === "post_call_success" && options && 'zoomLink' in options) {
    return generatePostCallSuccessEmail(lead, options as PostCallSuccessOptions, sender);
  }
  if (emailType === "custom" && options && 'body' in options) {
    return generateCustomEmail(lead, options as CustomEmailOptions, sender);
  }
  return generateFollowupEmail(lead, sender);
}
